import { useState } from "react";
import CryptoJS from "crypto-js";
import { LogIn } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

const AdminLogin = () => {
  const encryptionKey = "dgmxcvdkjdhfcnsdhfcoashfSS";
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  //Validation
  const validate = () => {
    const { email, password } = formData;
    let validationErrors = {};

    // Email validation
    if (!email) {
      validationErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      validationErrors.email = "Email is invalid";
    }

    // Password validation
    if (!password) {
      validationErrors.password = "Password is required";
    } else if (password.length < 6) {
      validationErrors.password = "Password must be at least 6 characters";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    setLoading(true);
   
    if (!encryptionKey) {
      throw new Error(
        "Encryption key is missing in the environment variables."
      );
    }
    // Encrypt the password
    const encryptedPassword = CryptoJS.AES.encrypt(
      formData.password,
      encryptionKey
    ).toString();
    try {
      const res = await api.post("/login", {
        email: formData.email,
        password: encryptedPassword,
      });
  
      const data = res.data;

      sessionStorage.setItem(
        "user",
        JSON.stringify({ id: data.id, role: data.role, token: data.token })
      );

      toast({
        title: "Login Successful",
        description: "Welcome back.",
      });

      navigate("/admin");
    } catch (error) {
      if (error.response?.status === 403) {
        toast({
          title: "Access Denied",
          description:
            error.response.data.message ||
            "You are not authorized to access the admin panel.",
          variant: "destructive",
          duration: 5000,
        });
      } else if (error.response?.status === 401) {
        toast({
          title: "Login Failed",
          description: "Invalid email or password.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "Error",
          description: "Something went wrong. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Clear field error while typing
    setErrors({
      ...errors,
      [e.target.name]: "",
    });
  };

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="bg-card p-8 md:p-10 rounded-3xl shadow-soft">
          <h3 className="text-2xl font-serif font-bold mb-6">
            Login to Admin Dashboard
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="username"
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium mb-2"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Forgot password link */}
            <div className="text-right">
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                onClick={() => navigate("/admin/forgot-password")}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-hero w-full flex items-center justify-center gap-2"
            >
              <LogIn size={18} />
              <span>{loading ? "Logging in..." : "Login"}</span>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default AdminLogin;
